/**
 * Read-only preview of submitted faculty feedback.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';

const LABELS = {
  TEACHING_QUALITY: 'Teaching quality',
  SUBJECT_KNOWLEDGE: 'Subject knowledge',
  COMMUNICATION: 'Communication',
  PUNCTUALITY: 'Punctuality',
  DOUBT_RESOLUTION: 'Doubt resolution',
  COURSE_COVERAGE: 'Course coverage',
  ASSESSMENT_FAIRNESS: 'Assessment fairness',
};

const MOOD = ['', '😩', '😕', '😐', '😊', '🔥'];

export default function StudentFeedbackPreview() {
  const { submissionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/feedback/${submissionId}`)
      .then((res) => setData(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.error?.message || 'Could not load this submission.'))
      .finally(() => setLoading(false));
  }, [submissionId]);

  return (
    <Layout>
      <div className="genz-page">
        <div className="genz-inner">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/80 mb-2">Locked preview</p>
          <h1 className="text-3xl font-semibold text-white m-0">Feedback preview</h1>
          <p className="genz-lock mt-3">Read-only. Submitted feedback cannot be edited.</p>

          {error ? <div className="alert-error mt-4">{error}</div> : null}
          {loading ? (
            <div className="genz-card mt-6">Loading preview…</div>
          ) : data ? (
            <div className="genz-card mt-6 space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-white m-0">{data.subject_name} · {data.subject_code}</h2>
                <p className="text-slate-300 mt-1 mb-0">
                  {data.faculty_name} · {data.cycle_name} · submitted {data.submitted_at ? new Date(data.submitted_at).toLocaleString() : '—'}
                </p>
              </div>
              <div className="space-y-3">
                {(data.ratings || []).map((row) => (
                  <div key={row.criterion} className="genz-criterion">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-white font-medium m-0">{LABELS[row.criterion] || row.criterion}</p>
                      <span className="text-lg">{MOOD[Number(row.rating)] || ''} {row.rating}/5</span>
                    </div>
                    {row.comments ? <p className="text-sm text-slate-300 m-0">{row.comments}</p> : null}
                  </div>
                ))}
              </div>
              <dl className="grid gap-3 sm:grid-cols-2">
                <div className="profile-field"><dt className="text-slate-400">Overall</dt><dd className="text-white">{data.overall_rating ?? '—'}</dd></div>
                <div className="profile-field"><dt className="text-slate-400">Anonymous</dt><dd className="text-white">{data.is_anonymous ? 'Yes' : 'No'}</dd></div>
                <div className="profile-field"><dt className="text-slate-400">Strengths</dt><dd className="text-white">{data.strengths || '—'}</dd></div>
                <div className="profile-field"><dt className="text-slate-400">Improvements</dt><dd className="text-white">{data.improvements || '—'}</dd></div>
                <div className="profile-field sm:col-span-2"><dt className="text-slate-400">Additional comments</dt><dd className="text-white">{data.additional_comments || '—'}</dd></div>
              </dl>
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
